//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';

import {
  IContentLibraryFiltersOptions,
  MediaFilterParamKey,
} from '@apis/content-library/contentLibraryApiModel';
import {
  getDefaultCampaignSubTypeFilterSection,
  getDefaultCampaignTypeFilterSection,
} from '@components/media-filters/CampaignsFilter';
import {
  getDateRangeLabel,
  isValidDateRange,
} from '@components/media-filters/DateRangeFilter';
import {
  FilterSection,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import {
  DEFAULT_HAS_PRODUCTS_FILTER_SECTION,
  DEFAULT_PRODUCT_TAGS_FILTER_SECTION,
  DEFAULT_SELECTED_PRODUCTS_FILTER_SECTION,
  MediaDefaultFilterSections,
  MediaLibrarySearchTagPrefixes,
} from '@components/media-filters/mediaFilterConstants';
import {
  sectionToSelectedStringValues,
  sectionToSelectedValue,
} from '@components/media-filters/useContentLibraryParamsMapper';
import { isDefined, isUndefined } from '@utils/filterPredicates';

import { hasFeature } from 'app/initialization/routing/stateHelpers';

// Fetch section from generic map and cast to appropriate type
export const getSection = <T extends FilterValue>(
  sections: Map<MediaFilterParamKey, FilterSection<FilterValue>>,
  paramKey: MediaFilterParamKey,
): FilterSection<T> | undefined => {
  return sections.get(paramKey) as FilterSection<T> | undefined;
};

/**
 * Map filter params for API request to corresponding sections for filter panel UI.
 * @param filters The filter params for API request
 * @returns A map of filter parameter keys to their corresponding filter sections
 */
export const mapParamsToSections = (
  filters: IContentLibraryFiltersOptions,
  isRetailerClient: boolean | undefined,
) => {
  const sections = new Map<MediaFilterParamKey, FilterSection<FilterValue>>();

  (Object.keys(filters) as MediaFilterParamKey[]).forEach((filterKey) => {
    // TODO: PD-297467 - Remove content_library_products_improvements feature flag
    const filterSections: Partial<
      Record<MediaFilterParamKey, FilterSection<FilterValue>>
    > = hasFeature(FeatureNames.ContentLibraryProductsImprovements)
      ? {
          ...MediaDefaultFilterSections,
          hasProducts: DEFAULT_HAS_PRODUCTS_FILTER_SECTION,
          productIds: DEFAULT_SELECTED_PRODUCTS_FILTER_SECTION,
          campaignSubTypes:
            getDefaultCampaignSubTypeFilterSection(isRetailerClient),
          campaignTypes: getDefaultCampaignTypeFilterSection(
            hasFeature(FeatureNames.CreatorCampaigns),
          ),
        }
      : {
          ...MediaDefaultFilterSections,
          hasProducts: DEFAULT_PRODUCT_TAGS_FILTER_SECTION,
          campaignSubTypes:
            getDefaultCampaignSubTypeFilterSection(isRetailerClient),
          campaignTypes: getDefaultCampaignTypeFilterSection(
            hasFeature(FeatureNames.CreatorCampaigns),
          ),
        };
    const section = structuredClone(filterSections[filterKey]);

    if (isUndefined(section)) {
      return;
    }

    // PD-301894: Remove excludeTargetedRetailerCampaigns and excludeNonTargetedRetailerCampaigns
    switch (filterKey) {
      case 'createdAfter':
      case 'filter':
      case 'hasLabels':
      case 'hasProducts':
      case 'isDistributed':
      case 'isScheduled':
      case 'itemId':
      case 'searchText':
      case 'excludeNonTargetedRetailerCampaignItems':
      case 'excludeTargetedRetailerCampaignItems':
      case 'spamFiltering': {
        const filterValue = filters[filterKey];

        if (isDefined(filterValue)) {
          section.options.forEach((option) => {
            if (filterValue === option.value) {
              option.isSelected = true;
            }
          });
        }

        break;
      }

      case 'dateRange': {
        const filterValue = filters[filterKey];

        if (isDefined(filterValue) && isValidDateRange(filterValue)) {
          const option = section.options.find(
            (option) => filterValue === option.value,
          );

          if (isDefined(option)) {
            option.isSelected = true;
          } else {
            section.options.push({
              isSelected: true,
              label: getDateRangeLabel(filterValue),
              value: filterValue,
            });
          }
        }

        break;
      }
      case 'distribution': {
        const isDistributed = filters.isDistributed;

        if (isDefined(isDistributed)) {
          const filterValues = filters[filterKey] ?? [];

          section.options = filterValues.map((value) => ({
            isSelected: true,
            label: value,
            value,
          }));
        }
        break;
      }
      case 'campaignTypes':
      case 'campaignSubTypes':
      case 'itemTypes':
      case 'mediaItemTypes':
      case 'networks':
      case 'orientation':
      case 'rejectionType':
      case 'rightsStatuses':
      case 'userIds': {
        const filterValues = filters[filterKey] ?? [];

        if (filterValues.length > 0) {
          section.options.forEach((option) => {
            if (filterValues.includes(option.value as string)) {
              option.isSelected = true;
            }
          });
        }

        break;
      }
      case 'igHashtags':
      case 'igUsernames': {
        const filterValues = filters[filterKey] ?? [];

        section.options = filterValues.map((value) => ({
          isSelected: true,
          label: `${MediaLibrarySearchTagPrefixes[filterKey]}${value}`,
          value,
        }));

        break;
      }
      case 'labels':
      case 'productIds': {
        const filterValues = filters[filterKey] ?? [];

        section.options = filterValues.map((value) => ({
          isSelected: true,
          label: value,
          value,
        }));

        break;
      }
      case 'semanticTags': {
        const filterValues = filters[filterKey] ?? [];

        section.options = filterValues.map(({ tag }) => ({
          isSelected: true,
          label: `${MediaLibrarySearchTagPrefixes[filterKey]}${tag}`,
          value: tag,
        }));

        break;
      }
    }

    sections.set(filterKey, section);
  });

  return sections;
};

export const mapSectionToFilterOption = (
  section: FilterSection<FilterValue>,
): IContentLibraryFiltersOptions => {
  // PD-301894: Remove excludeTargetedRetailerCampaigns and excludeNonTargetedRetailerCampaigns
  switch (section.paramKey) {
    case 'dateRange':
    case 'excludeNonTargetedRetailerCampaignItems':
    case 'excludeTargetedRetailerCampaignItems':
    case 'isDistributed':
    case 'hasLabels':
    case 'hasProducts':
    case 'spamFiltering':
      return { [section.paramKey]: sectionToSelectedValue(section) };
    case 'campaignTypes':
    case 'campaignSubTypes':
    case 'distribution':
    case 'labels':
    case 'igHashtags':
    case 'igUsernames':
    case 'mediaItemTypes':
    case 'networks':
    case 'orientation':
    case 'productIds':
    case 'userIds':
    case 'rejectionType':
    case 'rightsStatuses':
      return {
        [section.paramKey]: sectionToSelectedStringValues(section),
      };
    case 'semanticTags':
      return {
        [section.paramKey]: sectionToSelectedStringValues(section).map(
          (tag) => ({ tag }),
        ),
      };
    default:
      return {};
  }
};
